import { inject, Service } from "@angular/core";
import { Frequency, Streak } from "../types/supabase-types";
import { DateHelperService } from "./date-helper.service";

@Service()
export class StreakService {
  private readonly dateHelperService = inject(DateHelperService);

  public sortAndUpdateStreaks(streaks: Streak[] | null): Streak[] | null {
    streaks?.sort((a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    const updatedStreaks = streaks
      ? this.checkForStreakUpdates(streaks)
      : streaks;

    return updatedStreaks;
  }

  public checkForStreakUpdates(streaks: Streak[]): Streak[] {
    return streaks.map((streak) => this.checkForStreakUpdate(streak));
  }

  public checkForStreakUpdate(streak: Streak): Streak {
    const lastCountUpdatedAtDate = new Date(streak.count_updated_at);

    if (streak.frozen) {
      return streak;
    } else if (
      this.shouldResetStreak(streak.frequency, lastCountUpdatedAtDate)
    ) {
      return {
        ...streak,
        current_count: 0,
        current_activity_target_count: 0,
      };
    }
    return this.updateStreak(streak, lastCountUpdatedAtDate);
  }

  private updateStreak(
    streak: Streak,
    lastCountUpdatedAtDate: Date,
  ): Streak {
    switch (streak.frequency) {
      case "daily":
        if (this.dateHelperService.isYesterday(lastCountUpdatedAtDate)) {
          return {
            ...streak,
            current_activity_target_count: 0,
          };
        }

        return streak;
      case "weekly":
        const lastActivityTargetCountUpdatedAt = new Date(
          streak.activity_target_count_updated_at,
        );

        return {
          ...streak,
          current_activity_target_count: this.dateHelperService.isCurrentWeek(
              lastActivityTargetCountUpdatedAt,
            )
            ? streak.current_activity_target_count
            : 0,
        };

      case "monthly":
        throw new Error("Monthly streaks are not supported yet");
      default:
        throw new Error("Invalid frequency");
    }
  }

  private shouldResetStreak(
    frequency: Frequency,
    lastCountUpdatedAtDate: Date,
  ): boolean {
    switch (frequency) {
      case "daily":
        return !this.dateHelperService.isTodayOrYesterday(
          lastCountUpdatedAtDate,
        ) && !this.dateHelperService.isInFuture(lastCountUpdatedAtDate);
      case "weekly":
        return !this.dateHelperService.isCurrentOrPreviousWeek(
          lastCountUpdatedAtDate,
        ) && !this.dateHelperService.isInFuture(lastCountUpdatedAtDate);

      default:
        throw new Error("Invalid frequency");
    }
  }
}
